/**
 * PipeHydraulics.js
 * Darcy-Weisbach pipe head loss calculations.
 *
 * All internal calculations use Imperial units:
 *   flow rate  → GPM
 *   diameter   → inches (converted to ft internally)
 *   length     → feet
 *   temperature → °F
 *   head loss  → feet
 *   velocity   → ft/s
 *   viscosity  → ft²/s
 */

// ---------------------------------------------------------------------------
// Water kinematic viscosity lookup table [tempF, ν (ft²/s)]
// Source: Engineering Toolbox / ASHRAE Fundamentals
// ---------------------------------------------------------------------------
const WATER_KINEMATIC_VISCOSITY = [
  [32,  1.931e-5],
  [40,  1.664e-5],
  [50,  1.407e-5],
  [60,  1.217e-5],
  [70,  1.059e-5],
  [80,  0.930e-5],
  [100, 0.739e-5],
  [120, 0.609e-5],
  [140, 0.514e-5],
  [160, 0.442e-5],
  [180, 0.385e-5],
  [200, 0.341e-5],
  [212, 0.317e-5],
];

/**
 * Linear interpolation helper.
 * @param {Array<[number, number]>} table - sorted [x, y] pairs
 * @param {number} x
 * @returns {number} interpolated y
 */
function tableInterp(table, x) {
  if (x <= table[0][0]) return table[0][1];
  if (x >= table[table.length - 1][0]) return table[table.length - 1][1];
  for (let i = 0; i < table.length - 1; i++) {
    const [x0, y0] = table[i];
    const [x1, y1] = table[i + 1];
    if (x >= x0 && x <= x1) {
      const t = (x - x0) / (x1 - x0);
      return y0 + t * (y1 - y0);
    }
  }
  return table[table.length - 1][1];
}

/**
 * Returns kinematic viscosity of water at the given temperature.
 * @param {number} tempF - temperature in °F
 * @returns {number} kinematic viscosity in ft²/s
 */
export function getWaterKinematicViscosity(tempF) {
  return tableInterp(WATER_KINEMATIC_VISCOSITY, tempF);
}

// ---------------------------------------------------------------------------
// Pipe wall roughness (absolute roughness in feet)
// Copper drawn tubing: ε ≈ 5×10⁻⁶ ft (1.5 μm)
// Steel Sch 40 commercial: ε ≈ 1.5×10⁻⁴ ft (45 μm)
// PEX smooth plastic: ε ≈ 7×10⁻⁶ ft (2.1 μm)
// ---------------------------------------------------------------------------
const PIPE_ROUGHNESS_FT = {
  'Copper': 5.0e-6,
  'Steel':  1.5e-4,
  'PEX':    7.0e-6,
};

/**
 * Returns absolute pipe wall roughness in feet for a given material.
 * @param {string} material - 'Copper', 'Steel', or 'PEX'
 * @returns {number} roughness in feet
 */
export function getPipeRoughness(material) {
  return PIPE_ROUGHNESS_FT[material] ?? 5.0e-6;
}

// ---------------------------------------------------------------------------
// Friction factor (Darcy)
// ---------------------------------------------------------------------------

/**
 * Calculates the Darcy friction factor.
 *
 * Regime selection:
 *   Re < 2300              → laminar:      f = 64 / Re
 *   Re ≥ 4000              → turbulent:    Swamee-Jain approximation (explicit)
 *   2300 ≤ Re < 4000       → transitional: linear interpolation between regimes
 *
 * Swamee-Jain (1976):
 *   f = 0.25 / [ log10( ε/(3.7·D) + 5.74/Re^0.9 ) ]²
 *
 * @param {number} Re       - Reynolds number (dimensionless)
 * @param {number} epsilon  - absolute roughness (ft)
 * @param {number} D_ft     - internal diameter (ft)
 * @returns {number} Darcy friction factor
 */
export function calcFrictionFactor(Re, epsilon, D_ft) {
  if (Re <= 0) return 0;

  const f_laminar = (Re) => 64 / Re;

  const f_turbulent = (Re) => {
    const relRoughness = epsilon / D_ft;
    const inner = relRoughness / 3.7 + 5.74 / Math.pow(Re, 0.9);
    return 0.25 / Math.pow(Math.log10(inner), 2);
  };

  if (Re < 2300) {
    return f_laminar(Re);
  } else if (Re >= 4000) {
    return f_turbulent(Re);
  } else {
    // Transitional zone: linearly blend laminar and turbulent
    const t = (Re - 2300) / (4000 - 2300);
    return f_laminar(2300) * (1 - t) + f_turbulent(4000) * t;
  }
}

// ---------------------------------------------------------------------------
// Darcy-Weisbach head loss
// ---------------------------------------------------------------------------

/**
 * Calculates pipe friction head loss using the Darcy-Weisbach equation.
 *
 *   h_f = f · (L / D) · (v² / 2g)
 *
 * Fluid properties are taken from water at the given temperature. When a
 * glycol mixture is in use the flow rate fed in has already been corrected
 * for reduced heat capacity, and water properties give a conservative
 * (slightly lower-viscosity) approximation.
 *
 * @param {number} flowRate_GPM   - flow rate in US gallons per minute
 * @param {number} diameterIn     - pipe internal diameter in inches
 * @param {number} pipeLengthFt   - pipe length in feet
 * @param {number} tempF          - average fluid temperature in °F
 * @param {string} material       - pipe material ('Copper', 'Steel', 'PEX')
 * @returns {{ headLoss_ft: number, reynolds: number, frictionFactor: number }}
 */
export function calcDarcyWeisbachHead(flowRate_GPM, diameterIn, pipeLengthFt, tempF, material) {
  if (flowRate_GPM <= 0 || diameterIn <= 0 || pipeLengthFt <= 0) {
    return { headLoss_ft: 0, reynolds: 0, frictionFactor: 0 };
  }

  const g_ft_s2 = 32.174;               // ft/s²
  const D_ft = diameterIn / 12;         // convert inches → feet
  const A_ft2 = Math.PI * (D_ft / 2) ** 2;

  // Velocity (ft/s)
  const flowFt3s = flowRate_GPM / 448.831;
  const velocity = flowFt3s / A_ft2;

  // Kinematic viscosity of water at fluid temperature
  const nu = getWaterKinematicViscosity(tempF);       // ft²/s

  // Reynolds number: Re = v·D / ν
  const Re = velocity * D_ft / nu;

  // Darcy friction factor
  const epsilon = getPipeRoughness(material);
  const f = calcFrictionFactor(Re, epsilon, D_ft);

  // Darcy-Weisbach: h_f = f·(L/D)·(v²/2g)
  const headLoss_ft = f * (pipeLengthFt / D_ft) * (velocity ** 2 / (2 * g_ft_s2));

  return { headLoss_ft, reynolds: Re, frictionFactor: f };
}
